import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { SubItemCategory } from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';

interface SubItemCategoriesResponse {
  items: SubItemCategory[];
}

interface SubItemCategoryResponse {
  item: SubItemCategory;
}

export const subItemCategoryKeys = {
  all: ['sub-item-categories'] as const,
};

export function useSubItemCategories() {
  return useQuery({
    queryKey: subItemCategoryKeys.all,
    queryFn: async () => {
      const response = await apiClient.get<SubItemCategoriesResponse>('/sub-item-categories');
      return response.items;
    },
  });
}

export function useAddSubItemCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ label, pricingMode, taxApplicable }: { label: string; pricingMode: 'qty_rate' | 'amount'; taxApplicable?: boolean }) => {
      const response = await apiClient.post<SubItemCategoryResponse>('/sub-item-categories', { label, pricingMode, taxApplicable });
      return response.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subItemCategoryKeys.all });
      toast({ title: 'Category added' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to add category.', variant: 'destructive' });
    },
  });
}

export function useUpdateSubItemCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; label?: string; pricingMode?: 'qty_rate' | 'amount'; markupApplicable?: boolean; taxApplicable?: boolean; sortOrder?: number }) => {
      const response = await apiClient.put<SubItemCategoryResponse>(`/sub-item-categories/${id}`, updates);
      return response.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subItemCategoryKeys.all });
      toast({ title: 'Category updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to update category.', variant: 'destructive' });
    },
  });
}

export function useDeleteSubItemCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/sub-item-categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subItemCategoryKeys.all });
      toast({ title: 'Category deleted' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete category.', variant: 'destructive' });
    },
  });
}

